/**
 * Vesting utilities
 *
 * Parses vesting condition strings to extract grant-value fields and
 * aggregates them across share classes. Conditions strings look like:
 *   "Stock Price @ Grant: $123.83<br>Original Grant Value: $29,987.25<br>Forecasted Value: $34,107.21"
 *
 * Extracted from HeadlineService in #5198 Phase 6 — vesting math is a
 * separate concern from headline assembly.
 */

/**
 * Parse a money value from a formatted string like "$29,987.25"
 * @param {string} str - String containing money value
 * @returns {number|null} - Parsed number or null if not found
 */
export function parseMoney(str) {
	if (!str || typeof str !== 'string') return null;
	const cleaned = str.replace(/[$,]/g, '');
	const num = parseFloat(cleaned);
	return isNaN(num) ? null : num;
}

/**
 * Extract grant values from a conditions string.
 *
 * @param {string} conditions - The conditions string from a vesting schedule entry
 * @returns {Object} - { stockPriceAtGrant, originalGrantValue, forecastedValue, actualGrantValue }
 */
export function parseGrantConditions(conditions) {
	const result = {
		stockPriceAtGrant: null,
		originalGrantValue: null,
		forecastedValue: null,
		actualGrantValue: null,
	};

	if (!conditions || typeof conditions !== 'string') return result;

	const stockPriceMatch = conditions.match(/Stock Price @ Grant:\s*(\$[\d,]+\.?\d*)/i);
	if (stockPriceMatch) {
		result.stockPriceAtGrant = parseMoney(stockPriceMatch[1]);
	}

	const originalMatch = conditions.match(/Original Grant Value:\s*(\$[\d,]+\.?\d*)/i);
	if (originalMatch) {
		result.originalGrantValue = parseMoney(originalMatch[1]);
	}

	const forecastMatch = conditions.match(/Forecasted Value:\s*(\$[\d,]+\.?\d*)/i);
	if (forecastMatch) {
		result.forecastedValue = parseMoney(forecastMatch[1]);
	}

	const actualMatch = conditions.match(/Actual Grant Value:\s*(\$[\d,]+\.?\d*)/i);
	if (actualMatch) {
		result.actualGrantValue = parseMoney(actualMatch[1]);
	}

	return result;
}

/**
 * Aggregate grant values from vesting schedules across share classes.
 *
 * @param {Object} userData - User data object
 * @param {string} client - Client code
 * @param {string[]} shareKeys - Array of share class keys
 * @returns {Object} - { totalOriginalGrantValue, totalForecastedValue, totalActualGrantValue }
 */
export function aggregateGrantValues(userData, client, shareKeys) {
	const result = {
		totalOriginalGrantValue: 0,
		totalForecastedValue: 0,
		totalActualGrantValue: 0,
	};

	if (!userData || !client || !shareKeys) return result;

	const userClientData = userData.clients?.[client];
	if (!userClientData?.shares) return result;

	shareKeys.forEach((key) => {
		const vesting = userClientData.shares[key]?.vesting;
		if (!vesting || typeof vesting !== 'object') return;

		Object.values(vesting).forEach((grant) => {
			if (!grant || typeof grant !== 'object') return;
			if (grant.cashedOut === true) return;

			const parsed = parseGrantConditions(grant.conditions);

			if (!grant.vested) {
				if (parsed.originalGrantValue) {
					result.totalOriginalGrantValue += parsed.originalGrantValue;
				}
				if (parsed.forecastedValue) {
					result.totalForecastedValue += parsed.forecastedValue;
				}
			} else {
				if (parsed.actualGrantValue) {
					result.totalActualGrantValue += parsed.actualGrantValue;
				}
			}
		});
	});

	return result;
}
